/**
 * ARCreditNoteDetail.jsx
 * ERP-FIN-02 Gate AR-03: Credit Note Detail — Approve, Issue, Apply, Void
 *
 * Route: /ar/credit-notes/:cnId
 */
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { arCreditNoteApi } from "../services/api";

const STATUS_COLORS = {
  DRAFT:             { bg: "#F3F4F6", text: "#374151" },
  APPROVED:          { bg: "#DBEAFE", text: "#1D4ED8" },
  ISSUED:            { bg: "#D1FAE5", text: "#065F46" },
  PARTIALLY_APPLIED: { bg: "#FEF3C7", text: "#92400E" },
  FULLY_APPLIED:     { bg: "#D1FAE5", text: "#065F46" },
  VOID:              { bg: "#F3F4F6", text: "#6B7280" },
  REJECTED:          { bg: "#FEE2E2", text: "#991B1B" },
};

function StatusPill({ status }) {
  const c = STATUS_COLORS[status] || { bg: "#F3F4F6", text: "#374151" };
  return (
    <span data-testid="cn-status-pill" style={{
      background: c.bg, color: c.text, borderRadius: "12px",
      padding: "3px 12px", fontSize: "13px", fontWeight: 600,
    }}>
      {status?.replace(/_/g, " ")}
    </span>
  );
}

function fmtAmt(minor, currency = "INR") {
  const v = (minor || 0) / 100;
  return currency === "INR"
    ? `₹${v.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
    : `${currency} ${v.toFixed(2)}`;
}

export default function ARCreditNoteDetail() {
  const { cnId }   = useParams();
  const navigate   = useNavigate();
  const { user }   = useAuth();

  const [cn,        setCn]      = useState(null);
  const [auditLog,  setAuditLog] = useState([]);
  const [activeTab, setActiveTab] = useState("details");
  const [loading,   setLoading] = useState(true);
  const [error,     setError]   = useState(null);
  const [actionErr, setActionErr] = useState(null);

  // Modal states
  const [showApprove,  setShowApprove]  = useState(false);
  const [showReject,   setShowReject]   = useState(false);
  const [showVoid,     setShowVoid]     = useState(false);
  const [showApply,    setShowApply]    = useState(false);
  const [showRefund,   setShowRefund]   = useState(false);

  const canApprove = user?.capabilities?.includes("ar.credit_note.approve");
  const canIssue   = user?.capabilities?.includes("ar.credit_note.issue");
  const canVoid    = user?.capabilities?.includes("ar.credit_note.void");
  const canApply   = user?.capabilities?.includes("ar.credit_note.apply");
  const canRefund  = user?.capabilities?.includes("ar.refund.request");
  const canView    = user?.capabilities?.includes("ar.credit_note.view");

  const isSelf = cn?.created_by === user?.id;

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const res = await arCreditNoteApi.get(cnId);
      setCn(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load credit note");
    } finally {
      setLoading(false);
    }
  }, [cnId, canView]);

  const loadAudit = useCallback(async () => {
    try {
      const res = await arCreditNoteApi.getAudit(cnId);
      setAuditLog(res.data || []);
    } catch { /* non-fatal */ }
  }, [cnId]);

  useEffect(() => { load(); loadAudit(); }, [load, loadAudit]);

  async function handleIssue() {
    setActionErr(null);
    try {
      await arCreditNoteApi.issue(cnId, {});
      await load();
    } catch (e) {
      setActionErr(e.response?.data?.detail || "Issue failed");
    }
  }

  if (loading) return <div style={{ padding: "48px", textAlign: "center", color: "#6B7280" }}>Loading credit note…</div>;
  if (error)   return <div style={{ padding: "24px" }}><div style={errorBox}>{error}</div></div>;
  if (!cn)     return null;

  const tabs = [
    { key: "details",      label: "Details" },
    { key: "applications", label: `Applications (${cn.applications?.length || 0})` },
    { key: "audit",        label: "Audit Trail" },
  ];

  return (
    <div data-testid="ar-credit-note-detail" style={{ padding: "24px", maxWidth: "1100px", margin: "0 auto" }}>

      {/* Back + header */}
      <button onClick={() => navigate("/ar/credit-notes")} style={backBtn}>← Credit Notes</button>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px", flexWrap: "wrap", gap: "12px" }}>
        <div>
          <h1 data-testid="cn-number-heading" style={{ fontSize: "22px", fontWeight: 700, color: "#111827", margin: 0, fontFamily: "monospace" }}>
            {cn.credit_number}
          </h1>
          <div style={{ marginTop: "6px", display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            <StatusPill status={cn.status} />
            <span style={{ color: "#6B7280", fontSize: "13px" }}>
              Original: <span style={{ fontFamily: "monospace" }}>{cn.original_invoice_number}</span>
            </span>
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {cn.status === "APPROVED" && canIssue && (
            <button data-testid="issue-cn-btn" onClick={handleIssue} style={btnSuccess}>Issue Credit Note</button>
          )}
          {cn.status === "DRAFT" && canApprove && !isSelf && (
            <button data-testid="approve-cn-btn" onClick={() => setShowApprove(true)} style={btnPrimary}>Approve</button>
          )}
          {cn.status === "DRAFT" && canApprove && !isSelf && (
            <button data-testid="reject-cn-btn" onClick={() => setShowReject(true)} style={btnDanger}>Reject</button>
          )}
          {["ISSUED", "PARTIALLY_APPLIED"].includes(cn.status) && canApply && cn.unapplied_amount_minor > 0 && (
            <button data-testid="apply-cn-btn" onClick={() => setShowApply(true)} style={btnPrimary}>Apply Credit</button>
          )}
          {cn.status === "ISSUED" && canRefund && cn.unapplied_amount_minor > 0 && (
            <button data-testid="request-refund-btn" onClick={() => setShowRefund(true)} style={btnOutline}>Request Refund</button>
          )}
          {!["VOID", "REJECTED"].includes(cn.status) && canVoid && (
            <button data-testid="void-cn-btn" onClick={() => setShowVoid(true)} style={btnDanger}>Void</button>
          )}
          {cn.pdf_document_id && (
            <button
              data-testid="view-pdf-btn"
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/ar/credit-notes/${cnId}/pdf`, "_blank")}
              style={btnOutline}
            >
              PDF
            </button>
          )}
        </div>
      </div>

      {actionErr && <div style={errorBox}>{actionErr}</div>}

      {/* Tabs */}
      <div style={{ display: "flex", gap: "4px", marginBottom: "20px", borderBottom: "1px solid #E5E7EB" }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            ...tabBtn,
            borderBottom: activeTab === t.key ? "2px solid #111827" : "2px solid transparent",
            color: activeTab === t.key ? "#111827" : "#6B7280",
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "details" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          <InfoCard label="Customer" value={cn.customer_name || cn.customer_id} />
          <InfoCard label="Original Invoice" value={cn.original_invoice_number} mono />
          <InfoCard label="Credit Amount" value={fmtAmt(cn.credit_amount_minor, cn.currency_code)} />
          <InfoCard label="Unapplied Balance" value={fmtAmt(cn.unapplied_amount_minor, cn.currency_code)} />
          <InfoCard label="Applied Amount" value={fmtAmt(cn.applied_amount_minor, cn.currency_code)} />
          <InfoCard label="Credit Date" value={cn.credit_date} />
          <InfoCard label="Accounting Date" value={cn.accounting_date} />
          <InfoCard label="Credit Reason Code" value={cn.credit_reason_code || "—"} />
          <InfoCard label="Reason" value={cn.credit_reason} fullWidth />
          {cn.approved_by && <InfoCard label="Approved By" value={`${cn.approved_by_email} on ${(cn.approved_at || "").slice(0, 10)}`} />}
          {cn.issued_at && <InfoCard label="Issued At" value={(cn.issued_at || "").slice(0, 10)} />}
          {cn.void_reason && <InfoCard label="Void Reason" value={cn.void_reason} fullWidth />}
        </div>
      )}

      {activeTab === "applications" && (
        <div>
          {cn.applications?.length === 0 ? (
            <p style={{ color: "#9CA3AF", textAlign: "center", padding: "32px" }}>No credit applications yet</p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: "8px", overflow: "hidden" }}>
              <thead>
                <tr style={{ background: "#F9FAFB" }}>
                  {["Date", "Invoice ID", "Amount", "Status", "Created By"].map((h, i) => (
                    <th key={i} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(cn.applications || []).map(app => (
                  <tr key={app.id} data-testid={`app-row-${app.id}`}>
                    <td style={tdStyle}>{(app.application_date || "").slice(0, 10)}</td>
                    <td style={{ ...tdStyle, fontFamily: "monospace", fontSize: "12px" }}>{app.invoice_id}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>{fmtAmt(app.application_amount_minor)}</td>
                    <td style={tdStyle}>
                      <span style={{ color: app.is_reversed ? "#991B1B" : "#065F46", fontWeight: 600, fontSize: "12px" }}>
                        {app.is_reversed ? "REVERSED" : "APPLIED"}
                      </span>
                    </td>
                    <td style={tdStyle}>{app.created_by_email || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === "audit" && (
        <AuditTab auditLog={auditLog} />
      )}

      {/* Modals */}
      {showApprove && (
        <ConfirmModal
          title="Approve Credit Note"
          onClose={() => setShowApprove(false)}
          onConfirm={async (notes) => {
            await arCreditNoteApi.approve(cnId, { approval_notes: notes });
            setShowApprove(false);
            await load();
          }}
          inputLabel="Approval notes (optional)"
          confirmLabel="Approve"
          confirmStyle={btnPrimary}
          testId="approve-modal"
        />
      )}
      {showReject && (
        <ConfirmModal
          title="Reject Credit Note"
          onClose={() => setShowReject(false)}
          onConfirm={async (reason) => {
            await arCreditNoteApi.reject(cnId, { rejection_reason: reason });
            setShowReject(false);
            await load();
          }}
          inputLabel="Rejection reason (required)"
          confirmLabel="Reject"
          confirmStyle={btnDanger}
          required
          testId="reject-modal"
        />
      )}
      {showVoid && (
        <ConfirmModal
          title="Void Credit Note"
          onClose={() => setShowVoid(false)}
          onConfirm={async (reason) => {
            await arCreditNoteApi.void(cnId, { void_reason: reason });
            setShowVoid(false);
            await load();
          }}
          inputLabel="Void reason (required)"
          confirmLabel="Void"
          confirmStyle={btnDanger}
          required
          testId="void-modal"
        />
      )}
      {showApply && (
        <ApplyCreditModal
          cn={cn}
          onClose={() => setShowApply(false)}
          onApplied={async () => { setShowApply(false); await load(); await loadAudit(); }}
        />
      )}
      {showRefund && (
        <CreateRefundModal
          cn={cn}
          onClose={() => setShowRefund(false)}
          onCreated={(rf) => { setShowRefund(false); navigate(`/ar/refunds/${rf.id}`); }}
        />
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function InfoCard({ label, value, mono, fullWidth }) {
  return (
    <div style={{ gridColumn: fullWidth ? "1 / -1" : undefined, background: "#F9FAFB", borderRadius: "6px", padding: "12px 16px" }}>
      <div style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6B7280", marginBottom: "4px" }}>{label}</div>
      <div style={{ fontSize: "15px", color: "#111827", fontFamily: mono ? "monospace" : undefined }}>{value || "—"}</div>
    </div>
  );
}

function AuditTab({ auditLog }) {
  if (!auditLog.length) return <p style={{ color: "#9CA3AF", textAlign: "center", padding: "32px" }}>No audit events</p>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {auditLog.map(ev => (
        <div key={ev.id} style={{ background: "#F9FAFB", borderRadius: "6px", padding: "10px 14px", border: "1px solid #E5E7EB" }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{ev.event_type}</span>
            <span style={{ fontSize: "12px", color: "#6B7280" }}>{(ev.created_at || "").slice(0, 16).replace("T", " ")}</span>
          </div>
          <div style={{ fontSize: "13px", color: "#6B7280", marginTop: "2px" }}>{ev.summary || ""}</div>
          <div style={{ fontSize: "11px", color: "#9CA3AF", marginTop: "2px" }}>{ev.actor_email || ""}</div>
        </div>
      ))}
    </div>
  );
}

function ConfirmModal({ title, onClose, onConfirm, inputLabel, confirmLabel, confirmStyle, required, testId }) {
  const [value,   setValue]   = useState("");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (required && !value.trim()) { setError("This field is required"); return; }
    setLoading(true);
    setError(null);
    try {
      await onConfirm(value.trim());
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Action failed");
      setLoading(false);
    }
  }

  return (
    <div style={modalOverlay}>
      <div style={{ ...modalBox, width: "420px" }} data-testid={testId}>
        <h2 style={{ margin: "0 0 16px", fontSize: "17px", fontWeight: 700 }}>{title}</h2>
        {error && <div style={errorBox}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <label style={labelStyle}>{inputLabel}</label>
          <textarea
            style={{ ...inputStyle, height: "72px", resize: "vertical" }}
            value={value}
            onChange={e => setValue(e.target.value)}
          />
          <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "12px" }}>
            <button type="button" onClick={onClose} style={btnOutline}>Cancel</button>
            <button type="submit" disabled={loading} style={confirmStyle}>{loading ? "Working…" : confirmLabel}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ApplyCreditModal({ cn, onClose, onApplied }) {
  const [invoiceId, setInvoiceId]       = useState("");
  const [amountRupees, setAmountRupees] = useState("");
  const [loading,   setLoading]         = useState(false);
  const [error,     setError]           = useState(null);
  const maxAmt = (cn.unapplied_amount_minor || 0) / 100;

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const amount = Math.round(parseFloat(amountRupees) * 100);
      if (amount > cn.unapplied_amount_minor) {
        throw new Error(`Amount exceeds unapplied balance of ₹${maxAmt.toFixed(2)}`);
      }
      await arCreditNoteApi.apply(cn.id, {
        invoice_id:               invoiceId.trim(),
        application_amount_minor: amount,
      });
      onApplied();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Application failed");
      setLoading(false);
    }
  }

  return (
    <div style={modalOverlay}>
      <div style={{ ...modalBox, width: "440px" }} data-testid="apply-credit-modal">
        <h2 style={{ margin: "0 0 4px", fontSize: "17px", fontWeight: 700 }}>Apply Credit to Invoice</h2>
        <p style={{ color: "#6B7280", fontSize: "13px", marginBottom: "16px" }}>
          Unapplied balance: <strong>{fmtAmt(cn.unapplied_amount_minor, cn.currency_code)}</strong>
        </p>
        {error && <div style={errorBox}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={formRow}>
            <label style={labelStyle}>Invoice ID</label>
            <input data-testid="apply-invoice-id-input" style={inputStyle} value={invoiceId} onChange={e => setInvoiceId(e.target.value)} placeholder="Invoice UUID" required />
          </div>
          <div style={formRow}>
            <label style={labelStyle}>Amount (₹) <span style={{ color: "#6B7280", fontWeight: 400 }}>max ₹{maxAmt.toFixed(2)}</span></label>
            <input data-testid="apply-amount-input" style={inputStyle} type="number" step="0.01" min="0.01" max={maxAmt} value={amountRupees} onChange={e => setAmountRupees(e.target.value)} required />
          </div>
          <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
            <button type="button" onClick={onClose} style={btnOutline}>Cancel</button>
            <button data-testid="apply-confirm-btn" type="submit" disabled={loading} style={btnPrimary}>{loading ? "Applying…" : "Apply Credit"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreateRefundModal({ cn, onClose, onCreated }) {
  const [form, setForm] = useState({
    refund_amount_minor: ((cn.unapplied_amount_minor || 0) / 100).toFixed(2),
    refund_reason:       "",
    refund_method:       "",
    payee_name:          cn.customer_name || "",
  });
  const [methods,  setMethods]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  useEffect(() => {
    arCreditNoteApi.getMDMConfig("REFUND_METHOD").then(r => setMethods(r.data.items || []));
  }, []);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await arCreditNoteApi.createRefund({
        customer_id:         cn.customer_id,
        legal_entity_id:     cn.legal_entity_id,
        credit_note_id:      cn.id,
        refund_amount_minor: Math.round(parseFloat(form.refund_amount_minor) * 100),
        refund_reason:       form.refund_reason,
        refund_method:       form.refund_method,
        payee_name:          form.payee_name,
        currency_code:       cn.currency_code || "INR",
      });
      onCreated(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Failed");
      setLoading(false);
    }
  }

  return (
    <div style={modalOverlay}>
      <div style={{ ...modalBox, width: "440px" }} data-testid="create-refund-modal">
        <h2 style={{ margin: "0 0 16px", fontSize: "17px", fontWeight: 700 }}>Request Refund</h2>
        {error && <div style={errorBox}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={formRow}><label style={labelStyle}>Refund Amount (₹)</label>
            <input data-testid="refund-amount-input" style={inputStyle} type="number" step="0.01" min="0.01" value={form.refund_amount_minor} onChange={e => f("refund_amount_minor", e.target.value)} required />
          </div>
          <div style={formRow}><label style={labelStyle}>Payee Name</label>
            <input style={inputStyle} value={form.payee_name} onChange={e => f("payee_name", e.target.value)} required />
          </div>
          <div style={formRow}><label style={labelStyle}>Refund Method</label>
            <select data-testid="refund-method-select" style={inputStyle} value={form.refund_method} onChange={e => f("refund_method", e.target.value)} required>
              <option value="">-- Select --</option>
              {methods.map(m => <option key={m.item_code} value={m.item_code}>{m.display_name}</option>)}
            </select>
          </div>
          <div style={formRow}><label style={labelStyle}>Reason</label>
            <input data-testid="refund-reason-input" style={inputStyle} value={form.refund_reason} onChange={e => f("refund_reason", e.target.value)} required />
          </div>
          <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
            <button type="button" onClick={onClose} style={btnOutline}>Cancel</button>
            <button data-testid="refund-submit-btn" type="submit" disabled={loading} style={btnPrimary}>{loading ? "Creating…" : "Create Refund"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const btnPrimary  = { background: "#111827", color: "#fff", border: "none", borderRadius: "6px", padding: "8px 16px", fontSize: "14px", cursor: "pointer", fontWeight: 600 };
const btnSuccess  = { background: "#065F46", color: "#fff", border: "none", borderRadius: "6px", padding: "8px 16px", fontSize: "14px", cursor: "pointer", fontWeight: 600 };
const btnDanger   = { background: "#B91C1C", color: "#fff", border: "none", borderRadius: "6px", padding: "8px 16px", fontSize: "14px", cursor: "pointer", fontWeight: 600 };
const btnOutline  = { background: "#fff", color: "#374151", border: "1px solid #D1D5DB", borderRadius: "6px", padding: "8px 16px", fontSize: "14px", cursor: "pointer" };
const backBtn     = { background: "none", border: "none", cursor: "pointer", color: "#6B7280", fontSize: "14px", marginBottom: "16px", padding: "0" };
const tabBtn      = { background: "none", border: "none", padding: "8px 16px", fontSize: "14px", cursor: "pointer", fontWeight: 500, borderRadius: 0 };
const thStyle     = { padding: "10px 14px", fontSize: "12px", fontWeight: 600, color: "#6B7280", textAlign: "left", textTransform: "uppercase", letterSpacing: "0.05em" };
const tdStyle     = { padding: "12px 14px", fontSize: "14px", color: "#111827", borderTop: "1px solid #F3F4F6" };
const errorBox    = { background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B", borderRadius: "6px", padding: "10px 14px", marginBottom: "12px", fontSize: "14px" };
const modalOverlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
const modalBox    = { background: "#fff", borderRadius: "12px", padding: "24px", maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto" };
const formRow     = { marginBottom: "14px" };
const labelStyle  = { display: "block", fontSize: "13px", fontWeight: 600, color: "#374151", marginBottom: "4px" };
const inputStyle  = { width: "100%", border: "1px solid #D1D5DB", borderRadius: "6px", padding: "8px 10px", fontSize: "14px", boxSizing: "border-box" };
